/**
 * CONSENT PAGE COMPONENT - Medical Consent Form Management
 * 
 * This component provides a comprehensive consent form interface for medical
 * data collection and processing. It includes multiple consent sections
 * covering data privacy, medical monitoring, and patient rights.
 * 
 * Key Features:
 * - Multi-section consent form with accordion-style navigation
 * - Individual consent checkboxes for each section
 * - Patient and guardian information collection
 * - Form validation and submission handling
 * - Responsive design with sidebar navigation
 * - Progress tracking through consent sections
 * 
 * Consent Sections:
 * - Study Purpose and Objectives
 * - General Project Description
 * - Study Participation Details
 * - Time Requirements
 * - Risks and Precautions
 * - Compensation Information
 * - Confidentiality and Privacy
 * - Participation and Withdrawal Rights
 * - Applicable Regulations
 * - Contact Information
 * - Consent Declaration
 * 
 * User Roles:
 * - PATIENT: Can complete consent for themselves
 * - PARENT: Can complete consent on behalf of child
 * - DOCTOR: Can view and manage patient consent status
 */

// React core imports for component functionality and state management
import React, { useRef, useState, useEffect } from 'react';
import { getDoctorConsent, submitDoctorConsent } from '../api/doctorConsent';

// Page navigation type definition for application routing
type Page = 'login' | 'register' | 'dashboard' | 'history' | 'timeline' | 'settings' | 'consent' | 'patients';

/**
 * CONSENT PAGE PROPS INTERFACE
 * 
 * Defines the props structure for the ConsentPage component,
 * including navigation callbacks and user role information.
 */
type ConsentPageProps = {
  onLogout: () => void;           // Callback function for user logout
  onNavigate: (page: Page) => void;  // Callback function for page navigation
  userRole?: string;              // Current user's role (patient/parent/doctor)
  isDemo?: boolean;               // Whether this is a demo user
};

const CARD_TITLES = [
  'Σκοπός της μελέτης',
  'Γενική Περιγραφή του έργου',
  'Συμμετοχή στη μελέτη',
  'Χρόνος που απαιτείται',
  'Κίνδυνοι - Προφυλάξεις',
  'Αποζημίωση',
  'Εμπιστευτικότητα',
  'Συμμετοχή και Αποχώρηση',
  'Εφαρμοζόμενοι Κανονισμοί',
  'Στοιχεία Επικοινωνίας',
  'Δήλωση συγκατάθεσης',
];

const ConsentPage: React.FC<ConsentPageProps> = ({ onLogout, onNavigate, userRole, isDemo = false }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [openIdx, setOpenIdx] = useState(0);
  const itemRefs = useRef<Array<HTMLDivElement | null>>([]);
  const [consentChecks, setConsentChecks] = useState<boolean[]>(new Array(10).fill(false));
  const [fullName, setFullName] = useState('');
  const [guardianName, setGuardianName] = useState('');
  const [childName, setChildName] = useState('');

  // Doctor consent (Platform Terms + Clinical Acknowledgment) – only for logged-in doctors
  const isDoctorConsentFlow = userRole === 'doctor' && !isDemo;
  const [doctorConsentLoading, setDoctorConsentLoading] = useState(isDoctorConsentFlow);
  const [doctorHasConsent, setDoctorHasConsent] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [clinicalChecks, setClinicalChecks] = useState<boolean[]>([false, false, false, false]);
  const [clinicalAcknowledged, setClinicalAcknowledged] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  const handleConsentScroll = () => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    const atBottom = scrollHeight - scrollTop - clientHeight < 40;
    if (atBottom) setHasScrolledToBottom(true);
  };

  useEffect(() => {
    if (!isDoctorConsentFlow) return;
    const email = localStorage.getItem('currentUserEmail');
    if (!email) {
      setDoctorConsentLoading(false);
      return;
    }
    getDoctorConsent(email)
      .then((data) => {
        setDoctorHasConsent(!!data.hasConsent);
        setDoctorConsentLoading(false);
      })
      .catch(() => setDoctorConsentLoading(false));
  }, [isDoctorConsentFlow]);

  const CARD_CONTENTS: React.ReactNode[] = [
    (
      <>
        <p>
          Σκοπός της μελέτης είναι να αξιολογηθεί η εμπειρία του χρήστη καθώς
          αλληλεπιδρά με το σύστημα «x(επι)ΒΛΕΨΙΣ» (σε όρους ευκολίας χρήσης,
          ευχρηστίας και αποδοτικότητας καθώς και της ορθής λειτουργίας της
          πλατφόρμας). Σκοπός του πειράματος είναι η αξιολόγηση του συστήματος
          και όχι του χρήστη.
        </p>
        <p>
          Το ΕΥΒΙ-ΙΠ-ΙΤΕ θα συντονίσει το έργο και τη μελέτη που έχει ως στόχο την
          ανάπτυξη ενός συστήματος παρακολούθησης βρεφών πολύ μικρής ηλικίας, το
          οποίο θα περιλαμβάνει τρία διακριτά υποσυστήματα ικανά για την
          καταγραφή μη επεμβατικών σημάτων από το βρεφικό κρεβάτι, την αναγνώριση
          δυνητικά παθολογικών καταστάσεων και την ειδοποίηση των γονέων/ιατρού
          σε περιπτώσεις που χρήζουν προσοχής ή ιατρικής παρέμβασης. Η προσοχή
          μας εστιάζεται σε βρέφη, μιας και σε αυτήν την ευαίσθητη ηλικία οι
          πιθανές αναπνοές/άπνοιες, οι καρδιακές αρρυθμίες αλλά και ορισμένες
          κατηγορίες επιληπτικών κρίσεων, αν εκδηλωθούν κατά τη διάρκεια του
          ύπνου μπορούν υπό προϋποθέσεις να απειλήσουν τη ζωή του βρέφους, αν δεν
          ανιχνευτούν εγκαίρως. Για τον λόγο αυτό προτείνουμε τον σχεδιασμό και
          την υλοποίηση ενός ολοκληρωμένου συστήματος ανίχνευσης/αναγνώρισης
          πιθανών παθολογικών καταστάσεων σε βρέφη, μέσω ενός «έξυπνου» στρώματος
          κρεβατιού, μιας κάμερας, κατάλληλα προσαρμοσμένης στην βρεφική κούνια,
          αισθητήρων υγρασίας, υπέρυθρων αισθητήρων και καταγραφής θερμοκρασίας.
          Όλα τα βιοσήματα θα καταγράφονται διαρκώς και θα αποστέλλονται για
          ανάλυση σε κατάλληλη υπολογιστική μονάδα πολύ μικρού μεγέθους, η οποία
          θα πραγματοποιεί την ανάλυση με χρήση εξελιγμένων αλγορίθμων μηχανικής
          μάθησης. Επιπρόσθετα, θα αναπτυχθεί κατάλληλη διεπαφή ενημέρωσης των
          γονέων, η οποία, ανάλογα με την κρισιμότητα των ευρημάτων, θα
          ενεργοποιεί σύστημα ειδοποίησης των γονέων ή/και του ιατρού. Η
          διεπαφή αυτή θα υλοποιηθεί σε περιβάλλον διαδικτύου, έξυπνου τηλεφώνου
          και έξυπνου ρολογιού. Τέλος, η παρούσα πρόταση περιλαμβάνει και την
          ανάπτυξη βάσης καταγραφής δεδομένων για μελλοντική χρήση και αναφορά.
        </p>
        <p>
          Το ΕΥΒΙ-ΙΠ-ΙΤΕ θα συντονίσει την παρούσα μελέτη, όπου ένας ικανός
          αριθμός χρηστών, εθελοντικά, θα συμμετέχουν στη φάση πιλοτικού
          ελέγχου της πλατφόρμας «x(επι)ΒΛΕΨΙΣ». Το σύστημα αυτό όπως και η
          παρούσα μελέτη δεν περιέχει καμία πράξη διάκρισης, επιθετικής ή
          αντικοινωνικής συμπεριφοράς και άδικης μεταχείρισης. Οι συμμετέχοντες
          της μελέτης αυτής παρακαλούνται επίσης να αξιολογήσουν τη συνολική
          απόδοση της πλατφόρμας.
        </p>
      </>
    ),
    (
      <>
        <p>
          Ο βασικός στόχος του έργου είναι η ανάπτυξη ενός ολοκληρωμένου συστήματος
          καταγραφής μη επεμβατικών σημάτων από το παιδικό κρεβάτι (κούνια) με στόχο
          τον υπολογισμό βιοδεικτών κατάλληλων να ανιχνεύουν δυνητικά παθολογικές
          καταστάσεις σε μια ηλικία (μέχρι ενός έτους) όπου αφενός δύσκολα γίνονται
          αντιληπτές αφ’ ετέρου μπορεί να είναι απειλητικές για την υγεία του βρέφους,
          όπως ο πυρετός, ο εμετός ή ένα αναπνευστικό σταμάτημα.
        </p>
        <p>
          Ο πρώτος χρόνος της ζωής ενός παιδιού είναι κρίσιμος όσον αφορά την έγκαιρη
          αναγνώριση και διάγνωση παθολογικών καταστάσεων που απαιτούν άμεση ανίχνευση
          και παρέμβαση. Πολλές φορές οι γονείς δεν μπορούν να εντοπίσουν έγκαιρα
          ενδεικτικά σημάδια παθολογίας στις κινήσεις και στους ήχους ενός βρέφους.
          Για παράδειγμα, πολλές μορφές επιληψίας δεν εκδηλώνονται απαραίτητα με έντονη
          κινητική σημειολογία, ενώ σπασμοί μπορεί να γίνονται αντιληπτοί μόνο από
          έμπειρους παρατηρητές. Αντίστοιχα, καταστάσεις όπως καρδιακές αρρυθμίες,
          ο εμετός, το σύνδρομο αιφνίδιου θανάτου, διαταραχές αναπνοής/άπνοιες ή
          κάποιες επιληπτικές κρίσεις αν εκδηλωθούν τη νύχτα μπορεί υπό προϋποθέσεις να
          απειλήσουν τη ζωή του βρέφους, αν δεν ανιχνευτούν εγκαίρως.
        </p>
        <p>
          Όσο επιτακτική είναι η ανάγκη για έγκαιρη ανίχνευση, άλλο τόσο σημαντικό είναι
          το σύστημα να μην είναι επεμβατικό ή ενοχλητικό. Ο ύπνος στα βρέφη είναι
          ιδιαίτερα σημαντικός για την ανάπτυξή τους και δεν θα έπρεπε να διαταράσσεται
          εύκολα. Για τον λόγο αυτό προτείνεται μια λύση βασισμένη σε αισθητήρες και
          φορητές συσκευές που δεν έρχονται σε άμεση επαφή με το σώμα του μωρού.
        </p>
        <p>
          Το «x(επι)ΒΛΕΨΙΣ» επικεντρώνεται στην ανάπτυξη ενός διακριτικού και
          μη‑επεμβατικού συστήματος παρακολούθησης βρεφών, κατάλληλου να ανιχνεύει και
          να προλαμβάνει παθολογικές καταστάσεις πιθανώς επικίνδυνες για την υγεία του
          παιδιού. Το σύστημα θα αποτελείται από μια σειρά φιλικών προς τον χρήστη,
          ευφυών ηλεκτρονικών αισθητήρων με επίκεντρο μια βιντεοκάμερα παρακολούθησης
          υψηλής ανάλυσης (baby monitor), τοποθετημένη πάνω από το παιδικό κρεβάτι και
          ένα «έξυπνο» στρώμα.
        </p>
        <p>
          Το ΙΤΕ, ως ερευνητικός οργανισμός παγκόσμιου κύρους, μαζί με το Πολυτεχνείο
          Κρήτης έχουν την απαραίτητη τεχνογνωσία και πολύχρονη εμπειρία τόσο στην
          επεξεργασία σήματος/εικόνας όσο και στην ανάπτυξη ολοκληρωμένων βιοϊατρικών
          προϊόντων και εφαρμογών υποστήριξης αποφάσεων. Η Ιατρική Σχολή του
          Πανεπιστημίου Κρήτης διαθέτει την καθοδήγηση για την υλοποίηση και αξιολόγηση
          του τελικού προϊόντος. Η εταιρία GNOSIS θα αναλάβει την ανάπτυξη του
          λογισμικού αναγνώρισης πιθανών παθολογικών καταστάσεων, ενώ η εταιρία CYTECH
          θα επιφορτιστεί με την ανάπτυξη της «έξυπνης» εφαρμογής ειδοποίησης και την
          προώθηση του προϊόντος.
        </p>
      </>
    ),
    (
      <>
        <p>
          Η συμμετοχή σας στην παρούσα μελέτη συνίσταται στην δοκιμή της λύσης και στην
          ποσοτική και ποιοτική εκτίμηση της αποδοτικότητάς της. Η παρακολούθηση θα
          γίνει υπό την επίβλεψη της ιατρικής ομάδας από την Ιατρική Σχολή του
          Πανεπιστημίου Κρήτης στο ΠΑΓΝΗ, σε ειδικό ασφαλή χώρο που ήδη υπάρχει και
          χρησιμοποιείται από το ιατρικό προσωπικό. Το βρέφος θα παρακολουθείται από το
          μη επεμβατικό σύστημα με την χρήση των αισθητήρων (κάμερας για τη συλλογή
          οπτικού υλικού – βίντεο, μικροφώνου για την καταγραφή ήχου, αισθητήρων για
          μετρήσεις αναπνοής/βαλλιστοκαρδιογραφήματος στο ανώστρωμα του κρεβατιού,
          θερμόμετρου υπέρυθρων για την από απόσταση μέτρηση θερμοκρασίας σώματος και
          ειδικού αισθητήρα υγρασίας στο ανώστρωμα). Κανένας αισθητήρας δεν έρχεται σε
          άμεση επαφή με το σώμα του μωρού.
        </p>
        <p>
          Η μελέτη δεν υποκαθιστά ούτε θα εμποδίσει το προβλεπόμενο πλάνο θεραπείας ή
          φροντίδας του βρέφους και δεν αποτελεί μέσο διάγνωσης, ούτε θα εμποδίσει σε
          οιαδήποτε περίπτωση το ιατρικό προσωπικό του ΠΑΓΝΗ. Η συμμετοχή μπορεί να
          διακοπεί ανά πάσα στιγμή, σύμφωνα με την κρίση του υπεύθυνου ιατρού
          παρακολούθησης, χωρίς το σύστημα να παρεμποδίσει το έργο αυτό.
        </p>
        <p>
          Το ΕΥΒΙ-ΙΠ-ΙΤΕ είναι υπεύθυνο για την παθητική καταγραφή των βιοσημάτων στον
          χρόνο κατά την διάρκεια παραμονής του βρέφους υπό παρακολούθηση. Το ΕΥΒΙ‑ΙΠ‑ΙΤΕ
          και ο επιστημονικός υπεύθυνος του ΠΑΓΝΗ θα ενημερώσουν τους γονείς από την
          αρχή για τυχόν απορίες, καθώς και για το πέρας της μελέτης. Οι γονείς/νόμιμοι
          κηδεμόνες, οι οποίοι θα δώσουν την συγκατάθεσή τους για τη συμμετοχή, διατηρούν
          το δικαίωμα απεμπλοκής του βρέφους από τη διαδικασία σε οποιαδήποτε χρονική
          στιγμή.
        </p>
        <p>
          Στο τέλος της διαδικασίας, εφόσον έχετε επιπλέον απορίες, θα σας παρουσιαστούν
          λεπτομερείς εξηγήσεις. Τα καταγεγραμμένα δεδομένα θα λάβουν έναν κωδικό
          αριθμό και η λίστα που συνδέει το όνομα του μωρού με τον κωδικό θα κρατηθεί σε
          κλειδωμένο και ασφαλές χώρο. Τα κωδικοποιημένα (ανώνυμα) δεδομένα θα
          αποθηκευτούν σε ασφαλή βάση δεδομένων στο χώρο του ΙΤΕ και θα χρησιμοποιηθούν
          αποκλειστικά στο πλαίσιο των ερευνητικών σκοπών της μελέτης. Κανένα προσωπικό
          ή ιατρικό δεδομένο δεν θα εξέλθει εκτός του ΙΤΕ. Εφόσον προτιμάτε να μην
          συμμετέχετε ή να μην συνεχίσετε, μπορείτε να το δηλώσετε στην παρακάτω δήλωση
          συγκατάθεσης.
        </p>
        <p>
          Σε μερικές περιπτώσεις μπορεί να ζητηθεί να προσκομίσετε επιπλέον πληροφορίες
          ως μέρος επακόλουθου πειράματος. Αν κάτι τέτοιο κριθεί απαραίτητο, η συμμετοχή
          σας σε δεύτερη φάση είναι απολύτως προαιρετική και εθελοντική.
        </p>
      </>
    ),
    (
      <>
        <p>
          Η παρούσα μελέτη αναμένεται να διαρκέσει για κάθε εγγραφή για 8 ώρες κατά την
          διάρκεια της νύχτας για κάθε συμμετέχοντα σε αυτή.
        </p>
      </>
    ),
    (
      <>
        <p>
          Η παρούσα μελέτη δεν παρουσιάζει κάποιο είδους κίνδυνο που συνδέεται με τους
          χρήστες.
        </p>
      </>
    ),
    // Αποζημίωση
    (
      <>
        <p>
          Η συμμετοχή στη συγκεκριμένη έρευνα είναι εθελοντική. Δεν προβλέπεται
          κάποιας μορφής χρηματική αποζημίωση για τους συμμετέχοντες εθελοντές.
          Το Εργαστήριο Υπολογιστικής Βιοϊατρικής του Ινστιτούτου Πληροφορικής
          του ΙΤΕ ευχαριστεί θερμά όλους τους συμμετέχοντες για την ανιδιοτελή
          συνεισφορά τους.
        </p>
      </>
    ),
    // Εμπιστευτικότητα
    (
      <>
        <p>
          Η συμμετοχή σας σε αυτή τη μελέτη θα παραμείνει εμπιστευτική, και η
          ταυτότητα του μωρού καθώς και των γονέων/νόμιμων κηδεμόνων δεν θα
          αποθηκευτεί ποτέ μαζί με ιατρικά/προσωπικά δεδομένα του. Τα δεδομένα θα
          λάβουν έναν μοναδικό κωδικό αριθμό, και η λίστα που συνδέει τα
          προσωπικά στοιχεία με τον κωδικό θα φυλάσσεται σε ασφαλή, κλειδωμένο
          χώρο.
        </p>
        <p>
          Όταν χρησιμοποιούνται τα δεδομένα, το όνομα του βρέφους ή των γονέων
          δεν θα παρουσιάζεται σε καμία περίπτωση. Τα καταγεγραμμένα δεδομένα θα
          συλλεχθούν και θα διατηρηθούν ανώνυμα, θα αποθηκευτούν σε ασφαλή βάση
          δεδομένων με περιορισμένη και ελεγχόμενη πρόσβαση στο ΙΤΕ και θα
          χρησιμοποιηθούν αποκλειστικά για ερευνητικούς σκοπούς, σύμφωνα με το
          άρθρο 89, παρ. 1 και 2 του ΓΚΠΔ EE/2016/679.
        </p>
        <p>
          Οι συμμετέχοντες έχουν δικαιώματα ενημέρωσης, πρόσβασης, διόρθωσης,
          διαγραφής, περιορισμού επεξεργασίας και εναντίωσης. Για την άσκηση των
          δικαιωμάτων μπορείτε να επικοινωνήσετε με τον Επιστημονικό Υπεύθυνο
          της μελέτης (Αρ. Ευάγγελο Σακκαλή) ή με τον Υπεύθυνο Προστασίας
          Δεδομένων του ΙΤΕ στο email: dpo@admin.forth.gr.
        </p>
      </>
    ),
    // Συμμετοχή και Αποχώρηση
    (
      <>
        <p>
          Η συμμετοχή στην παρούσα μελέτη γίνεται σε εθελοντική βάση και
          μπορείτε να αποχωρήσετε οποτεδήποτε το θελήσετε χωρίς καμία επίπτωση.
          Η άρνηση συμμετοχής ή η ανάκληση της συγκατάθεσης δεν θα έχει
          δυσμενείς συνέπειες για εσάς ή το περιβάλλον σας. Μπορείτε να
          αποχωρήσετε αφού δηλώσετε στην ερευνητική ομάδα ότι δεν επιθυμείτε να
          συνεχίσετε.
        </p>
        <p>
          Οι γονείς/νόμιμοι κηδεμόνες είναι ελεύθεροι ανά πάσα στιγμή να αρνηθούν
          τη συμμετοχή ή να την ανακαλέσουν χωρίς αρνητικές συνέπειες και χωρίς
          να απαιτείται αιτιολόγηση. Στην περίπτωση αυτή τα προσωπικά/ιατρικά
          δεδομένα καταστρέφονται πλήρως.
        </p>
      </>
    ),
    // Εφαρμοζόμενοι Κανονισμοί
    (
      <>
        <p>
          Η προστασία των προσωπικών δεδομένων και της ιδιωτικής ζωής αποτελεί
          θεμελιώδες ανθρώπινο δικαίωμα. Η επεξεργασία δεδομένων διέπεται από τον
          «Γενικό Κανονισμό Προστασίας Δεδομένων / General Data Protection
          Regulation (EE 679/2016)». Λαμβάνονται υπόψη όλοι οι ευρωπαϊκοί και
          ελληνικοί νόμοι και οδηγίες για την προστασία των δεδομένων προσωπικού
          χαρακτήρα.
        </p>
      </>
    ),
    // Στοιχεία Επικοινωνίας
    (
      <>
        <p>
          Ο Επιστημονικός Υπεύθυνος (ΕΥ) που εποπτεύει την παρούσα μελέτη είναι ο
          Δρ. Ευάγγελος Σακκαλής, Κύριος Ερευνητής με το Εργαστήριο Υπολογιστικής
          Βιοϊατρικής (EYBI) του Ινστιτούτου Πληροφορικής (ΙΠ) στο Ίδρυμα
          Τεχνολογίας και Έρευνας (ΙΤΕ).
        </p>
        <p>
          Στοιχεία επικοινωνίας με τον ΕΥ: sakkalis@ics.forth.gr, +30 2810 391448
          και στο Εργαστήριο Υπολογιστικής Βιοϊατρικής, Ινστιτούτο Πληροφορικής,
          Ίδρυμα Τεχνολογίας και Έρευνας, Βασιλικά Βουτών, Θυρίδα 1385,
          71110 Ηράκλειο, Κρήτη, Ελλάδα.
        </p>
      </>
    ),
    // Δήλωση συγκατάθεσης
    (
      <>
        <div style={{ color: '#555' }}>
          <p>
            Εγώ, ο/η
            <input
              className="inline-consent-input"
              type="text"
              value={guardianName}
              onChange={(e) => {
                const v = e.target.value;
                setGuardianName(v);
                setFullName(v);
              }}
            />
            , ο/η κάτωθι υπογεγραμμένος/η, νόμιμος κηδεμόνας/γονέας του παιδιού
            <input
              className="inline-consent-input"
              type="text"
              value={childName}
              onChange={(e) => setChildName(e.target.value)}
            />
            , έχω λάβει όλες τις απαραίτητες πληροφορίες, έχω διαβάσει και κατανοήσει τον σκοπό
            του έργου καθώς και τα δικαιώματά μου και δηλώνω:
          </p>
          {[
            'Δηλώνω την κατανόησή μου για την τήρηση των διαβεβαιώσεων για υπεύθυνη, συνετή και συνεπή διαχείριση δεδομένων σύμφωνα με τον ΓΚΠΔ.',
            'Αποδέχομαι ότι η συμμετοχή μου και τα προσωπικά δεδομένα μου αντιμετωπίζονται ως εμπιστευτικά στο πλαίσιο του προγράμματος.',
            'Κατανοώ ότι ο σκοπός της παρούσας μελέτης είναι η αξιολόγηση του συστήματος και όχι του χρήστη και ότι τα δεδομένα θα χρησιμοποιηθούν μόνο για ερευνητικούς σκοπούς.',
            'Κατανοώ ότι τα προσωπικά/ιατρικά δεδομένα δεν θα παραχωρηθούν σε τρίτο χωρίς εξουσιοδότηση και θα τηρούνται ανώνυμα σε ασφαλή βάση δεδομένων.',
            'Κατανοώ ότι από το βρέφος μου οι καταγραφές είναι παθητικές και δεν αποτελούν ιατρική διάγνωση.',
            'Διατηρώ το δικαίωμα να λάβω αντίγραφο του παρόντος και να αιτηθώ πρόσβαση στα δεδομένα, σύμφωνα με τον ΓΚΠΔ.',
            'Γνωρίζω ότι έχω δικαίωμα άρνησης συμμετοχής ή ανάκλησης συγκατάθεσης χωρίς συνέπειες.',
            'Αποδέχομαι ότι η συμμετοχή μου είναι απολύτως εθελοντική.',
            'Δεν επιθυμώ νέα επαφή για πιθανή δεύτερη φάση (προαιρετικό).',
          ].map((txt, i) => (
            <label key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, margin: '8px 0' }}>
              <input type="checkbox" checked={consentChecks[i]} onChange={() => toggleConsent(i)} />
              <span>{txt}</span>
            </label>
          ))}

          <div style={{ marginTop: 24 }}>
            <label className="input-label">Full Name</label>
            <input
              type="text"
              className="input-field"
              placeholder="Full Name"
              value={fullName}
              onChange={(e) => { const v = e.target.value; setFullName(v); setGuardianName(v); }}
            />
          </div>
        </div>
      </>
    ),
  ];
  const goNext = (idx: number) => {
    const next = Math.min(CARD_TITLES.length - 1, idx + 1);
    setOpenIdx(next);
    setTimeout(() => itemRefs.current[next]?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
  };
  const goPrev = (idx: number) => {
    const prev = Math.max(0, idx - 1);
    setOpenIdx(prev);
    setTimeout(() => itemRefs.current[prev]?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
  };

  const toggleConsent = (i: number) =>
    setConsentChecks((prev) => prev.map((v, idx) => (idx === i ? !v : v)));
  const resetConsent = () => setConsentChecks(new Array(10).fill(false));
  const canSubmit = consentChecks.slice(0, 9).every(Boolean) && fullName.trim().length > 0; // last checkbox optional

  return (
    <div className="dashboard">
      <header className="header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="hamburger-btn" onClick={() => setIsSidebarOpen(true)} aria-label="Open menu">☰</button>
          <h1>Consent</h1>
        </div>
        <div />
      </header>

      <div className={`sidebar ${isSidebarOpen ? 'open' : ''}`} role="navigation" aria-label="Main menu">

        <ul className="sidebar-menu">
          <li
            onClick={() => {
              setIsSidebarOpen(false);
              if (userRole === 'doctor' && !isDemo) {
                onNavigate('patients');
              } else {
                onNavigate('dashboard');
              }
            }}
          >
            <span className="mi">🏠</span><span>Overview</span>
          </li>
          <li onClick={() => { setIsSidebarOpen(false); onNavigate('history'); }}><span className="mi">🕒</span><span>History</span></li>
          {userRole === 'doctor' && !isDemo && (
            <li onClick={() => { setIsSidebarOpen(false); onNavigate('timeline'); }}><span className="mi">📈</span><span>Timeline</span></li>
          )}
          <li onClick={() => { setIsSidebarOpen(false); onNavigate('settings'); }}><span className="mi">⚙️</span><span>Settings</span></li>
          <li onClick={() => setIsSidebarOpen(false)}><span className="mi">📝</span><span>Consent</span></li>
          <li onClick={() => { setIsSidebarOpen(false); onLogout(); }}><span className="mi">↪</span><span>Logout</span></li>
        </ul>
        <div className="sidebar-footer">
          <div className="sidebar-brand"></div>
        </div>
      </div>
      <div className={`sidebar-backdrop ${isSidebarOpen ? 'show' : ''}`} onClick={() => setIsSidebarOpen(false)}></div>

      <div className="history-container">
        {isDoctorConsentFlow ? (
          doctorConsentLoading ? (
            <div className="consent-section" style={{ padding: 24, textAlign: 'center' }}>Loading...</div>
          ) : doctorHasConsent ? (
            <div className="doctor-consent-view consent-section" style={{ maxWidth: 720, margin: '0 auto', padding: 24 }}>
              <h2 style={{ marginBottom: 8, fontSize: '1.1rem' }}>A. Platform Terms of Use (EU + Greece, Class I MDR-aligned)</h2>
              <h3 style={{ marginBottom: 16, fontWeight: 600 }}>PLATFORM TERMS OF USE</h3>
              <p style={{ marginBottom: 12 }}><strong>(Electronic Acceptance Required)</strong></p>
              <p style={{ marginBottom: 12 }}>By clicking &quot;I Accept&quot;, you confirm that you are a licensed healthcare professional or an authorized representative of a healthcare organization, and that you agree to the following terms.</p>
              <h4 style={{ marginTop: 16, marginBottom: 8 }}>1. Platform Description and Intended Purpose</h4>
              <p style={{ marginBottom: 12 }}>The Platform is a software system for the visualization of patient vital signs and rule-based notification of values exceeding predefined thresholds.</p>
              <p style={{ marginBottom: 12 }}>The Platform: Presents data as received from external devices or manual input; Generates alerts based exclusively on static, user-defined thresholds; Does not perform automated clinical decision-making.</p>
              <p style={{ marginBottom: 12 }}>The Platform is intended to be used as a clinical information support tool.</p>
              <h4 style={{ marginTop: 16, marginBottom: 8 }}>2. Regulatory Status (EU MDR Alignment)</h4>
              <p style={{ marginBottom: 12 }}><strong>2.1</strong> The Platform is designed in accordance with Regulation (EU) 2017/745 (MDR) and is positioned as: Medical Device Software – Class I, or Non-medical decision support software, depending on deployment context and claims.</p>
              <p style={{ marginBottom: 12 }}><strong>2.2</strong> The Platform: Does not provide diagnostic or therapeutic recommendations; Does not modify or adapt clinical parameters autonomously; Allows full transparency of threshold logic.</p>
              <h4 style={{ marginTop: 16, marginBottom: 8 }}>3. No Medical Advice or Automation</h4>
              <p style={{ marginBottom: 12 }}>The Platform: Does not replace professional medical judgment; Does not guarantee detection of all critical events; Does not ensure continuous or real-time monitoring. Any alert generated by the Platform is informational only.</p>
              <h4 style={{ marginTop: 16, marginBottom: 8 }}>4. Clinical Responsibility</h4>
              <p style={{ marginBottom: 12 }}>By accepting these Terms, the User acknowledges that: All clinical decisions remain under the sole responsibility of the clinician; Configuration of thresholds is the User&apos;s responsibility; Interpretation and response to alerts require clinical assessment. The Platform Provider does not assume responsibility for patient outcomes.</p>
              <h4 style={{ marginTop: 16, marginBottom: 8 }}>5. Limitation of Liability</h4>
              <p style={{ marginBottom: 12 }}>To the maximum extent permitted by applicable law: The Platform Provider shall not be liable for: Missed, delayed, or false alerts; Clinical decisions made or not made based on Platform data; Errors originating from sensors, data transmission, or user input. The Platform is provided &quot;as is&quot; and &quot;as available&quot;.</p>
              <h4 style={{ marginTop: 16, marginBottom: 8 }}>6. Data Protection and Confidentiality</h4>
              <p style={{ marginBottom: 12 }}><strong>6.1</strong> Processing of personal and health data complies with GDPR (EU 2016/679). <strong>6.2</strong> The User confirms that appropriate patient consent or legal basis for data processing has been obtained.</p>
              <h4 style={{ marginTop: 16, marginBottom: 8 }}>7. Electronic Acceptance</h4>
              <p style={{ marginBottom: 12 }}>Acceptance of these Terms by electronic means constitutes a legally binding agreement equivalent to a handwritten signature, in accordance with: eIDAS Regulation (EU 910/2014); Greek Civil Code provisions on electronic contracts.</p>
              <h4 style={{ marginTop: 16, marginBottom: 8 }}>8. Governing Law</h4>
              <p style={{ marginBottom: 24 }}>These Terms shall be governed by the laws of Greece and applicable EU law.</p>
              <p style={{ marginBottom: 24 }}>✔ [I Accept]</p>

              <h2 style={{ marginBottom: 8, fontSize: '1.1rem', marginTop: 32 }}>B. Clinical Responsibility Acknowledgment (Embedded or Annex)</h2>
              <h3 style={{ marginBottom: 16, fontWeight: 600 }}>CLINICAL RESPONSIBILITY ACKNOWLEDGMENT</h3>
              <p style={{ marginBottom: 12 }}>By accepting, I confirm that:</p>
              <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
                <li style={{ marginBottom: 8 }}>☑ I understand that the Platform provides visualization and rule-based alerts only</li>
                <li style={{ marginBottom: 8 }}>☑ I acknowledge that the Platform does not provide diagnosis or treatment advice</li>
                <li style={{ marginBottom: 8 }}>☑ I remain fully responsible for: Clinical interpretation of data; Decisions and actions regarding patient care</li>
                <li style={{ marginBottom: 8 }}>☑ Alerts are supportive information and do not replace clinical judgment</li>
                <li style={{ marginBottom: 8 }}>☑ I understand the limitations of telemonitoring technologies</li>
              </ul>
              <p style={{ marginBottom: 0 }}>✔ [I Acknowledge and Accept]</p>
            </div>
          ) : (
            <div className="doctor-consent-form consent-section" style={{ maxWidth: 720, margin: '0 auto', padding: 24 }}>
              <h2 style={{ marginBottom: 8 }}>Platform Terms &amp; Clinical Acknowledgment</h2>
              <p style={{ marginBottom: 16, fontSize: '0.9rem', color: '#555' }}>
                Please scroll down to read all information. The acceptance checkboxes and submit button are at the bottom.
              </p>

              <div
                ref={scrollContainerRef}
                onScroll={handleConsentScroll}
                style={{
                  maxHeight: 420,
                  overflowY: 'auto',
                  padding: 16,
                  border: '1px solid #ddd',
                  borderRadius: 8,
                  marginBottom: 16,
                  fontSize: '0.95rem',
                }}
              >
                <section style={{ marginBottom: 24 }}>
                  <h3 style={{ marginBottom: 8, fontSize: '1.1rem' }}>A. Platform Terms of Use (EU + Greece, Class I MDR-aligned)</h3>
                  <h4 style={{ marginBottom: 12, fontWeight: 600 }}>PLATFORM TERMS OF USE</h4>
                  <p style={{ marginBottom: 8 }}><strong>(Electronic Acceptance Required)</strong></p>
                  <p style={{ marginBottom: 12 }}>By clicking &quot;I Accept&quot;, you confirm that you are a licensed healthcare professional or an authorized representative of a healthcare organization, and that you agree to the following terms.</p>
                  <p style={{ marginBottom: 8 }}><strong>1. Platform Description and Intended Purpose</strong><br />The Platform is a software system for the visualization of patient vital signs and rule-based notification of values exceeding predefined thresholds. The Platform: Presents data as received from external devices or manual input; Generates alerts based exclusively on static, user-defined thresholds; Does not perform automated clinical decision-making. The Platform is intended to be used as a clinical information support tool.</p>
                  <p style={{ marginBottom: 8 }}><strong>2. Regulatory Status (EU MDR Alignment)</strong><br />2.1 The Platform is designed in accordance with Regulation (EU) 2017/745 (MDR). 2.2 The Platform does not provide diagnostic or therapeutic recommendations; does not modify or adapt clinical parameters autonomously; allows full transparency of threshold logic.</p>
                  <p style={{ marginBottom: 8 }}><strong>3. No Medical Advice or Automation</strong><br />The Platform does not replace professional medical judgment; does not guarantee detection of all critical events; does not ensure continuous or real-time monitoring. Any alert is informational only.</p>
                  <p style={{ marginBottom: 8 }}><strong>4. Clinical Responsibility</strong><br />All clinical decisions remain under the sole responsibility of the clinician; configuration of thresholds is the User&apos;s responsibility; the Platform Provider does not assume responsibility for patient outcomes.</p>
                  <p style={{ marginBottom: 8 }}><strong>5. Limitation of Liability</strong><br />The Platform Provider shall not be liable for missed, delayed, or false alerts; clinical decisions made or not made based on Platform data; errors from sensors, data transmission, or user input. The Platform is provided &quot;as is&quot; and &quot;as available&quot;.</p>
                  <p style={{ marginBottom: 8 }}><strong>6. Data Protection and Confidentiality</strong><br />Processing complies with GDPR. The User confirms appropriate patient consent or legal basis for data processing has been obtained.</p>
                  <p style={{ marginBottom: 8 }}><strong>7. Electronic Acceptance / 8. Governing Law</strong><br />Electronic acceptance constitutes a legally binding agreement (eIDAS, Greek Civil Code). These Terms shall be governed by the laws of Greece and applicable EU law.</p>
                </section>

                <section style={{ marginBottom: 24 }}>
                  <h3 style={{ marginBottom: 12, fontSize: '1.1rem' }}>B. Clinical Responsibility Acknowledgment</h3>
                  <h4 style={{ marginBottom: 8, fontWeight: 600 }}>CLINICAL RESPONSIBILITY ACKNOWLEDGMENT</h4>
                  <p style={{ marginBottom: 12 }}>By accepting, I confirm that:</p>
                  <ul style={{ marginBottom: 16, paddingLeft: 20 }}>
                    <li style={{ marginBottom: 6 }}>I understand that the Platform provides visualization and rule-based alerts only</li>
                    <li style={{ marginBottom: 6 }}>I acknowledge that the Platform does not provide diagnosis or treatment advice</li>
                    <li style={{ marginBottom: 6 }}>I remain fully responsible for: Clinical interpretation of data; Decisions and actions regarding patient care</li>
                    <li style={{ marginBottom: 6 }}>Alerts are supportive information and do not replace clinical judgment</li>
                    <li style={{ marginBottom: 6 }}>I understand the limitations of telemonitoring technologies</li>
                  </ul>
                </section>

                <div style={{ paddingTop: 8, borderTop: '1px solid #eee' }}>
                  {!hasScrolledToBottom && (
                    <p style={{ marginBottom: 12, fontSize: '0.9rem', color: '#888' }}>Scroll to the bottom to accept.</p>
                  )}
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                    <input
                      type="checkbox"
                      checked={termsAccepted}
                      onChange={() => setTermsAccepted((v) => !v)}
                      disabled={!hasScrolledToBottom}
                    />
                    <span>I Accept the Platform Terms of Use</span>
                  </label>
                  {[
                    'I understand that the Platform provides visualization and rule-based alerts only',
                    'I acknowledge that the Platform does not provide diagnosis or treatment advice',
                    'I remain fully responsible for: Clinical interpretation of data; Decisions and actions regarding patient care',
                    'Alerts are supportive information and do not replace clinical judgment; I understand the limitations of telemonitoring technologies',
                  ].map((text, i) => (
                    <label key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 10 }}>
                      <input
                        type="checkbox"
                        checked={clinicalChecks[i]}
                        onChange={() => setClinicalChecks((prev) => prev.map((v, j) => (j === i ? !v : v)))}
                        disabled={!hasScrolledToBottom}
                      />
                      <span>{text}</span>
                    </label>
                  ))}
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
                    <input
                      type="checkbox"
                      checked={clinicalAcknowledged}
                      onChange={() => setClinicalAcknowledged((v) => !v)}
                      disabled={!hasScrolledToBottom || !clinicalChecks.every(Boolean)}
                    />
                    <span>I Acknowledge and Accept</span>
                  </label>
                </div>
              </div>

              <div className="modal-actions" style={{ marginTop: 16 }}>
                <button
                  className="btn-primary"
                  disabled={!termsAccepted || !clinicalAcknowledged || !clinicalChecks.every(Boolean) || submitLoading}
                  style={
                    !termsAccepted || !clinicalAcknowledged || !clinicalChecks.every(Boolean) || submitLoading
                      ? { background: '#9e9e9e', color: '#fff', cursor: 'not-allowed' }
                      : undefined
                  }
                  onClick={async () => {
                    const email = localStorage.getItem('currentUserEmail');
                    if (!email) return;
                    setSubmitLoading(true);
                    try {
                      await submitDoctorConsent(email);
                      setDoctorHasConsent(true);
                    } catch (e) {
                      alert((e as Error).message || 'Submission failed');
                    } finally {
                      setSubmitLoading(false);
                    }
                  }}
                >
                  {submitLoading ? 'Submitting...' : 'Submit consent'}
                </button>
              </div>
            </div>
          )
        ) : (
        <div className="accordion">
          {CARD_TITLES.map((title, idx) => (
            <div className="accordion-item" key={title} ref={(el) => (itemRefs.current[idx] = el)}>
              <button className="accordion-header" onClick={() => setOpenIdx(openIdx === idx ? -1 : idx)}>
                <span>{title}</span>
                <span>{openIdx === idx ? '▴' : '▾'}</span>
              </button>
              {openIdx === idx && (
                <div className="accordion-body">
                  <div className="consent-section">
                    {CARD_CONTENTS[idx] ?? (
                      <p style={{ marginBottom: 12 }}>
                        Παρακαλούμε διαβάστε την ενότητα και πατήστε «Next» για να
                        προχωρήσετε στην επόμενη.
                      </p>
                    )}
                    <div className="modal-actions">
                      {idx > 0 && (
                        <button className="btn-secondary" onClick={() => goPrev(idx)}>Previous</button>
                      )}
                      {idx < CARD_TITLES.length - 1 ? (
                        <button className="btn-primary" onClick={() => goNext(idx)}>Next</button>
                      ) : (
                        <>
                          <button className="btn-secondary" onClick={resetConsent}>Revoke</button>
                          <button
                            className="btn-primary"
                            onClick={() => {
                              if (!canSubmit) {
                                alert('Παρακαλούμε επιβεβαιώστε όλα τα πεδία και συμπληρώστε το ονοματεπώνυμο.');
                                return;
                              }
                              alert('Η δήλωση συγκατάθεσης υποβλήθηκε επιτυχώς. Ευχαριστούμε!');
                            }}
                          >
                            Submit
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
        )}
      </div>
    </div>
  );
};

export default ConsentPage;


